import type { ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router';
import { formatDate } from '@xdocs/i18n';
import type { CheckoutErrorReason } from '@xdocs/schemas';
import { Button, Mascot, cn, useI18n } from '@xdocs/ui';
import { useSubscription } from '../settings/billing/useSubscription.js';
import { useCheckoutT } from './useCheckoutT.js';
import styles from './CheckoutErrorPage.module.css';

function parseReason(raw: string | null): CheckoutErrorReason {
  return raw === 'payment_declined' || raw === 'past_due' ? raw : 'cancelled';
}

/**
 * `/checkout/error` (F46) — three distinct, non-collapsible cases per the
 * frozen `checkoutErrorReason` enum: `cancelled` (checkout abandoned),
 * `payment_declined` (Paddle declined), `past_due` (an EXISTING PRO
 * subscription's renewal failed — not a fresh checkout return; reachable
 * directly from `PlanCard`'s "Update payment method" link, not just a
 * Paddle redirect).
 */
export function CheckoutErrorPage(): ReactElement {
  const { locale } = useI18n();
  const { t } = useCheckoutT();
  const [searchParams] = useSearchParams();
  const reason = parseReason(searchParams.get('reason'));
  const { subscription } = useSubscription();

  if (reason === 'past_due') {
    return (
      <div className={cn(styles['page'])} role="alert">
        <Mascot pose="concerned" size={120} alt="" />
        <h1 className={cn(styles['title'])}>{t('checkout:error.pastDue.title')}</h1>
        <p className={cn(styles['description'])}>
          {subscription?.currentPeriodEnd
            ? t('checkout:error.pastDue.description', { date: formatDate(locale, subscription.currentPeriodEnd, { dateStyle: 'medium' }) })
            : t('checkout:error.pastDue.descriptionNoDate')}
        </p>
        <div className={cn(styles['actions'])}>
          {/* GAP (WIRING.md): no op in the frozen contract resolves a
              Paddle "update payment method" URL (e.g.
              `billing.paymentMethod.updateUrl`) — ux.md forbids a dead
              control, so this stays disabled-with-reason instead of a
              button that does nothing. */}
          <Button variant="primary" disabled disabledReason={t('checkout:error.pastDue.action')}>
            {t('checkout:error.pastDue.action')}
          </Button>
          <Link to="/settings/billing">
            <Button variant="ghost">{t('checkout:error.backToBilling')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (reason === 'payment_declined') {
    return (
      <div className={cn(styles['page'])} role="alert">
        <Mascot pose="concerned" size={120} alt="" />
        <h1 className={cn(styles['title'])}>{t('checkout:error.declined.title')}</h1>
        <p className={cn(styles['description'])}>{t('checkout:error.declined.description')}</p>
        <div className={cn(styles['actions'])}>
          <Link to="/settings/billing">
            <Button variant="primary">{t('checkout:error.declined.action')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  // cancelled — neutral, non-alarming copy (F46 AC).
  return (
    <div className={cn(styles['page'])}>
      <Mascot pose="idle" size={120} alt="" />
      <h1 className={cn(styles['title'])}>{t('checkout:error.cancelled.title')}</h1>
      <p className={cn(styles['description'])}>{t('checkout:error.cancelled.description')}</p>
      <div className={cn(styles['actions'])}>
        <Link to="/settings/billing">
          <Button variant="primary">{t('checkout:error.cancelled.action')}</Button>
        </Link>
      </div>
    </div>
  );
}
